import React, { useEffect, useRef } from "react";
import { HeroSectionStyled } from "./HeroSectionStyled";
import { gsap } from "gsap";

interface HeroSectionProps {
  title?: string;
  description?: string;
  image?: string;
}

const HeroSection = ({
  title = "",
  description = "",
  image = "",
}: HeroSectionProps) => {
  const sectionRef = useRef<HTMLElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    const section = sectionRef.current;
    const titleEl = titleRef.current;
    if (!section || !titleEl || !title) return;

    // Split title into words for animation
    const words = title.split(" ");
    titleEl.innerHTML = words
      .map(
        (word, index) =>
          `<span class="word ${index === words.length - 1 ? "highlight" : ""}" style="display: inline-block; opacity: 0; transform: translateY(50px);">${word}&nbsp;</span>`
      )
      .join("");

    const wordElements = titleEl.querySelectorAll(".word");

    const ctx = gsap.context(() => {
      // Label animation
      gsap.fromTo(
        section.querySelector(".heroLabel"),
        { opacity: 0, y: 20 },
        { opacity: 1, y: 0, duration: 0.6, ease: "power3.out", delay: 0.1 }
      );

      // Title words animation
      gsap.to(wordElements, {
        opacity: 1,
        y: 0,
        duration: 0.7,
        stagger: 0.1,
        ease: "power3.out",
        delay: 0.2,
      });

      // Description animation
      gsap.fromTo(
        section.querySelector(".heroDescription"),
        { opacity: 0, y: 30 },
        { opacity: 1, y: 0, duration: 0.7, ease: "power3.out", delay: 0.5 }
      );

      // Breadcrumb animation
      gsap.fromTo(
        section.querySelector(".breadcrumb"),
        { opacity: 0 },
        { opacity: 1, duration: 0.6, ease: "power3.out", delay: 0.7 }
      );

      // Floating shapes
      gsap.fromTo(
        section.querySelectorAll(".floatingShape"),
        { opacity: 0, scale: 0 },
        {
          opacity: 1,
          scale: 1,
          duration: 0.8,
          stagger: 0.15,
          ease: "back.out(1.7)",
          delay: 0.3,
        }
      );
    }, section);

    return () => ctx.revert();
  }, [title]);

  return (
    <HeroSectionStyled ref={sectionRef}>
      {/* Background decorations */}
      <div className="heroBg">
        <div className="gradientOrb orb1" />
        <div className="gradientOrb orb2" />
        <div className="gridPattern" />
      </div>

      {/* Floating shapes */}
      <div className="floatingShape shape1" />
      <div className="floatingShape shape2" />
      <div className="floatingShape shape3" />

      <div className="heroSectionContainer">
        <div className="heroContent">
          <div className="heroLabel">
            <span className="line" />
            <span>Our Services</span>
            <span className="line" />
          </div>

          <h1 className="heroTitle" ref={titleRef}>
            {title}
          </h1>

          {description && <p className="heroDescription">{description}</p>}

          <div className="breadcrumb">
            <a href="/">Home</a>
            <span className="separator">/</span>
            <a href="/#services">Services</a>
            <span className="separator">/</span>
            <span className="current">{title}</span>
          </div>
        </div>

        {image && (
          <div className="heroImageWrapper">
            <div className="imageGlow" />
            <img src={image} alt={title} />
          </div>
        )}
      </div>
    </HeroSectionStyled>
  );
};

export default HeroSection;
